import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2, Search } from 'lucide-react';

import surveyService from '../../../services/surveyService';

// Developer tool: every survey window in the system, grouped by visit
// (Development > Testing Tools). Built with the site's own styles so the
// website no longer needs react-admin and Material UI (FECOA-2175).

const STATUS_STYLES = {
  open: 'bg-success-500/10 text-success-600 dark:text-success-400 border-success-500/30',
  in_progress: 'bg-warning-500/10 text-warning-600 dark:text-warning-400 border-warning-500/30',
  pending: 'bg-warning-500/10 text-warning-600 dark:text-warning-400 border-warning-500/30',
  completed: 'bg-primary-500/10 text-primary-700 dark:text-primary-300 border-primary-500/30',
  expired: 'bg-danger-500/10 text-danger-600 dark:text-danger-400 border-danger-500/30',
  missed: 'bg-danger-500/10 text-danger-600 dark:text-danger-400 border-danger-500/30',
  upcoming: 'bg-info-500/10 text-info-600 dark:text-info-400 border-info-500/30',
  closed: 'bg-info-500/10 text-info-600 dark:text-info-400 border-info-500/30',
};
const NEUTRAL = 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-gray-300 border-gray-200 dark:border-slate-700';

const STATUS_OPTIONS = [
  ['', 'All'],
  ['open', 'Open'],
  ['pending', 'Pending'],
  ['completed', 'Completed'],
  ['missed', 'Missed'],
  ['closed', 'Closed'],
];
const PAGE_SIZES = [10, 25, 50, 100];

const Pill = ({ className, children }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${className}`}>
    {children}
  </span>
);

const fieldClass = 'input';

const groupByVisit = (windows) => {
  const grouped = {};
  (windows || []).forEach((window) => {
    const visit = window.visit_number || 'Ungrouped';
    if (!grouped[visit]) grouped[visit] = [];
    grouped[visit].push(window);
  });
  const visits = Object.keys(grouped).sort((a, b) => {
    if (a === 'Ungrouped') return 1;
    if (b === 'Ungrouped') return -1;
    return Number(a) - Number(b);
  });
  return { grouped, visits };
};

const AllSurveyWindows = () => {
  const [windowsData, setWindowsData] = useState({ windows: [], pagination: { count: 0, totalPages: 1 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchSurveyWindows = async () => {
    try {
      setLoading(true);
      const data = await surveyService.getAllSurveyWindows({
        page,
        pageSize,
        status: statusFilter,
        search: searchTerm,
      });
      setWindowsData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching survey windows:', err);
      setError('Failed to load survey windows. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Searching waits for Enter or the Search button; the other filters apply at once.
  useEffect(() => {
    fetchSurveyWindows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, statusFilter]);

  const runSearch = () => {
    setPage(1);
    fetchSurveyWindows();
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" aria-label="Loading survey windows" />
      </div>
    );
  }

  if (error) {
    return <p className="p-6 text-sm font-medium text-danger-600 dark:text-danger-400">{error}</p>;
  }

  const { grouped, visits } = groupByVisit(windowsData.windows);
  const totalPages = Math.max(1, windowsData.pagination.totalPages || 1);
  const goTo = (next) => setPage(Math.min(Math.max(1, next), totalPages));

  return (
    <div className="w-full space-y-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">All Survey Windows</h2>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
        <label className="md:col-span-3">
          <span className="label">Status</span>
          <select
            className={fieldClass}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            {STATUS_OPTIONS.map(([value, label]) => (
              <option key={value || 'all'} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <div className="md:col-span-6">
          <label htmlFor="all-windows-search" className="label">
            Search
          </label>
          <div className="flex gap-2">
            <input
              id="all-windows-search"
              className={fieldClass}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch();
              }}
              placeholder="Search by survey, participant ID, email, study"
            />
            <button
              type="button"
              onClick={runSearch}
              className="btn btn-primary"
            >
              <Search className="h-4 w-4" />
              Search
            </button>
          </div>
          <p className="hint">Use the full UUID for an exact participant match.</p>
        </div>
        <label className="md:col-span-3">
          <span className="label">Items per page</span>
          <select
            className={fieldClass}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </label>
      </div>

      {visits.length === 0 ? (
        <p className="text-sm text-gray-600 dark:text-gray-400">No survey windows found.</p>
      ) : (
        visits.map((visit) => (
          <section key={visit} className="space-y-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {visit === 'Ungrouped' ? 'Ungrouped Windows' : `Visit #${visit}`}
            </h3>
            <div className="card overflow-x-auto">
              <table className="min-w-[650px] w-full text-left text-sm" aria-label={`Survey windows for visit ${visit}`}>
                <thead>
                  <tr>
                    {['ID', 'Survey Name', 'Participant', 'Study', 'Start Date', 'End Date', 'Completion Date', 'Status', 'Activated'].map((heading) => (
                      <th key={heading} scope="col" className="th-cell">{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {grouped[visit].map((window) => (
                    <tr key={window.id} className="tr-interactive text-gray-900 dark:text-gray-100">
                      <th scope="row" className="td-cell font-normal tabular-nums">{window.id}</th>
                      <td className="td-cell">
                        <div>
                          {window.surveyName && !window.surveyName.startsWith('Survey #')
                            ? window.surveyName
                            : 'Survey Name Not Available'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">ID: {window.surveyId || 'N/A'}</div>
                      </td>
                      <td className="td-cell">
                        <div>{window.participantId || 'N/A'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{window.participantName}</div>
                      </td>
                      <td className="td-cell">{window.studyName}</td>
                      <td className="td-cell">{window.startDate}</td>
                      <td className="td-cell">{window.endDate}</td>
                      <td className="td-cell">{window.completionDate}</td>
                      <td className="td-cell">
                        <Pill className={STATUS_STYLES[window.status?.toLowerCase()] || NEUTRAL}>{window.status}</Pill>
                      </td>
                      <td className="td-cell">
                        <Pill className={window.isActivated ? STATUS_STYLES.open : NEUTRAL}>{window.isActivated ? 'Yes' : 'No'}</Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}

      <nav className="flex flex-wrap items-center justify-center gap-3" aria-label="Survey windows pages">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          Showing {windowsData.windows.length} of {windowsData.pagination.count} items
        </span>
        <div className="flex items-center gap-1">
          {[
            [ChevronsLeft, 'First page', 1],
            [ChevronLeft, 'Previous page', page - 1],
          ].map(([Icon, label, target]) => (
            <button
              key={label}
              type="button"
              aria-label={label}
              disabled={page <= 1}
              onClick={() => goTo(target)}
              className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-slate-800"
            >
              <Icon className="h-4 w-4" />
            </button>
          ))}
          <span className="px-2 text-sm text-gray-700 dark:text-gray-300">
            Page {page} of {totalPages}
          </span>
          {[
            [ChevronRight, 'Next page', page + 1],
            [ChevronsRight, 'Last page', totalPages],
          ].map(([Icon, label, target]) => (
            <button
              key={label}
              type="button"
              aria-label={label}
              disabled={page >= totalPages}
              onClick={() => goTo(target)}
              className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-slate-800"
            >
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default AllSurveyWindows;
